import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Schedule as ScheduleIcon,
  ContentCopy as ContentCopyIcon,
  AccountTree as AccountTreeIcon,
} from "@mui/icons-material";
import { AssessedSession } from "../../../redux/actions/assessmentActions";
import { getSessionStateColor } from "./assessmentHelpers";

interface AssessedSessionMetadataCardProps {
  currentAssessedSession: AssessedSession;
  sessionId?: string;
}

const AssessedSessionMetadataCard: React.FC<AssessedSessionMetadataCardProps> = ({
  currentAssessedSession,
  sessionId,
}) => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyId = (id: string, type: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(type);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getSessionStateIcon = (state: string) => {
    switch (state) {
      case "ready":
        return <CheckCircleIcon fontSize="small" />;
      case "active":
        return <RadioButtonUncheckedIcon fontSize="small" />;
      case "processing":
        return <HourglassEmptyIcon fontSize="small" />;
      case "created":
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  return (
    <>
      {/* Metadata Card */}
      <Card elevation={1}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Organization:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {currentAssessedSession.organization_id}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                User:
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() =>
                  navigate(
                    `/dashboard/users/${currentAssessedSession.user_id}`
                  )
                }
                sx={{
                  textTransform: "none",
                  p: 0.5,
                }}
              >
                {currentAssessedSession.user_id}
              </Button>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box display="flex" alignItems="center" gap={1}>
              <ChatIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Conversation:
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() =>
                  navigate(
                    `/dashboard/conversations/${currentAssessedSession.conversation_id}`
                  )
                }
                sx={{
                  textTransform: "none",
                  p: 0.5,
                }}
              >
                {currentAssessedSession.conversation_id}
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Chip
              icon={getSessionStateIcon(currentAssessedSession.state)}
              label={currentAssessedSession.state}
              size="small"
              color={
                getSessionStateColor(currentAssessedSession.state) as any
              }
            />
          </Box>
          <Box mt={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Session ID: {currentAssessedSession.chat_session_id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Created:{" "}
                  {currentAssessedSession._ts
                    ? new Date(
                        currentAssessedSession._ts * 1000
                      ).toLocaleString()
                    : currentAssessedSession.created_at
                    ? new Date(
                        currentAssessedSession.created_at
                      ).toLocaleString()
                    : "-"}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ChatIcon />}
                onClick={() =>
                  navigate(
                    `/dashboard/conversations/${currentAssessedSession.conversation_id}?target_id=msg:${currentAssessedSession.chat_session_id}:0`
                  )
                }
              >
                View Chat Session
              </Button>
            </Box>
            {sessionId && (
              <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Assessment Session ID:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    bgcolor: "grey.100",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: "0.7rem",
                  }}
                >
                  {sessionId}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    handleCopyId(sessionId, "assessmentSession")
                  }
                  sx={{ p: 0.25 }}
                >
                  {copiedId === "assessmentSession" ? (
                    <CheckCircleIcon
                      sx={{ fontSize: 14 }}
                      color="success"
                    />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
                {copiedId === "assessmentSession" && (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ fontSize: "0.7rem", ml: 0.5 }}
                  >
                    Copied!
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Analysis Graph Info */}
      {currentAssessedSession.analysis_graph_info && (
        <Card elevation={1}>
          <CardContent sx={{ py: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <AccountTreeIcon sx={{ fontSize: 18 }} color="primary" />
              <Typography variant="body2" fontWeight="medium">
                Analysis Configuration
              </Typography>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={3}>
              {/* Type */}
              {currentAssessedSession.analysis_graph_info.type && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Type
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {currentAssessedSession.analysis_graph_info.type}
                  </Typography>
                </Box>
              )}

              {/* Major Version */}
              {currentAssessedSession.analysis_graph_info.major_version !==
                undefined && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Major Version
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    fontFamily="monospace"
                  >
                    {
                      currentAssessedSession.analysis_graph_info
                        .major_version
                    }
                  </Typography>
                </Box>
              )}

              {/* Minor Version */}
              {currentAssessedSession.analysis_graph_info.minor_version !==
                undefined && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Minor Version
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    fontFamily="monospace"
                  >
                    {
                      currentAssessedSession.analysis_graph_info
                        .minor_version
                    }
                  </Typography>
                </Box>
              )}

              {/* Namespace */}
              {currentAssessedSession.analysis_graph_info.namespace && (
                <Box flex={1} minWidth={200}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Namespace
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      wordBreak: "break-all",
                    }}
                  >
                    {currentAssessedSession.analysis_graph_info.namespace}
                  </Typography>
                </Box>
              )}

              {/* Transaction Index */}
              {currentAssessedSession.analysis_graph_info
                .transaction_index !== undefined && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Transaction Index
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {
                      currentAssessedSession.analysis_graph_info
                        .transaction_index
                    }
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AssessedSessionMetadataCard;
